import { useId } from 'react'

import { useGuideTour } from '../provider/guideTourContext'
import {
  hasGuideTourStepTarget,
  isGuideTourStepTargetInteractionBlocked,
} from '../utils/targets'
import type { SpotlightBounds } from './utils'
import * as S from './styles'

const buildInterruptBackdropPanels = (bounds: SpotlightBounds) => [
  {
    key: 'top',
    style: {
      top: 0,
      left: 0,
      right: 0,
      height: bounds.top,
    },
  },
  {
    key: 'left',
    style: {
      top: bounds.top,
      left: 0,
      width: bounds.left,
      height: bounds.height,
    },
  },
  {
    key: 'right',
    style: {
      top: bounds.top,
      left: bounds.left + bounds.width,
      right: 0,
      height: bounds.height,
    },
  },
  {
    key: 'bottom',
    style: {
      top: bounds.top + bounds.height,
      left: 0,
      right: 0,
      bottom: 0,
    },
  },
]

export const GuideTourOverlay = () => {
  const {
    state,
    currentStep,
    interrupt,
    targetBounds,
    isCurrentStepReady,
    isInModalLayer,
    isSkippedStepsSummaryActive,
  } = useGuideTour()
  const maskId = useId()
  const hasTarget = hasGuideTourStepTarget(currentStep)
  const isActive = state.status === 'active'
  const isSummaryOnly = isSkippedStepsSummaryActive
  const allowsTargetInteraction =
    hasTarget &&
    currentStep !== null &&
    !isGuideTourStepTargetInteractionBlocked(currentStep)

  if (!isActive || (!isSummaryOnly && !isCurrentStepReady)) {
    return null
  }

  if (!isSummaryOnly && hasTarget && !targetBounds) {
    return null
  }

  const spotlight =
    hasTarget && targetBounds ? (
      <>
        <defs>
          <mask id={maskId}>
            <rect width="100%" height="100%" fill="white" />
            <rect
              x={targetBounds.left}
              y={targetBounds.top}
              width={targetBounds.width}
              height={targetBounds.height}
              rx={12}
              fill="black"
            />
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.65)"
          mask={`url(#${maskId})`}
        />
      </>
    ) : (
      <rect width="100%" height="100%" fill="rgba(0, 0, 0, 0.65)" />
    )

  if (allowsTargetInteraction && targetBounds) {
    return (
      <>
        <S.Overlay
          data-testid="guide-tour-overlay"
          elevated={isInModalLayer}
          $pointerEvents="none"
          aria-hidden
        >
          <S.OverlaySvg>{spotlight}</S.OverlaySvg>
        </S.Overlay>
        {buildInterruptBackdropPanels(targetBounds).map(panel => (
          <S.InterruptBackdrop
            key={panel.key}
            data-testid="guide-tour-interrupt-backdrop"
            elevated={isInModalLayer}
            style={panel.style}
            onClick={interrupt}
          />
        ))}
      </>
    )
  }

  return (
    <S.Overlay
      data-testid="guide-tour-overlay"
      elevated={isInModalLayer}
      aria-hidden
      onClick={interrupt}
    >
      <S.OverlaySvg>{spotlight}</S.OverlaySvg>
    </S.Overlay>
  )
}
